'use client';

import { sendGTMEvent } from '@next/third-parties/google';
import { usePathname } from 'next/navigation';
import { ReactNode } from 'react';

import { Link, LinkProps } from '@natu/next-link';
import { Button } from '@natu/ui/Button';
import { ButtonWithEvent } from '@natu/ui/Button/ButtonWithEvent';
import { getEventPayload } from '@natu/utils/getEventPayload';
import { usePageContext } from '@natu/utils/PageContextProvider';

interface LinkWithConditionalEventProps {
  className?: string;
  children: ReactNode;
  link: LinkProps;
  isButton?: boolean;
  descriptiveText?: string;
}

export const LinkWithConditionalEvent = ({
  className,
  children,
  link,
  isButton,
  descriptiveText,
}: LinkWithConditionalEventProps) => {
  const pathname = usePathname();
  const { isArticle, isCampaign } = usePageContext() || {};

  const linkProps = {
    ...link,
    className,
    ...(descriptiveText && { 'aria-label': descriptiveText }),
  };

  if (isArticle) {
    const isCaseStudy = (link?.href as string)?.includes('/portfolio');
    const event = isCaseStudy ? 'E_cta_blog_banner_to_case_study' : 'E_cta_blog_banner_landing';
    const eventPayload = getEventPayload({ pathname, event, isCampaign });

    if (isButton)
      return (
        <ButtonWithEvent
          className="w-max"
          size="sm"
          event={event}
          variant="outline-transparent"
          asChild
        >
          <Link {...linkProps}>{children}</Link>
        </ButtonWithEvent>
      );

    return (
      <Link {...linkProps} onClick={() => sendGTMEvent({ event, ...eventPayload })}>
        {children}
      </Link>
    );
  }

  if (isButton)
    return (
      <Button className="w-max" size="sm" variant="outline-transparent" asChild>
        <Link {...linkProps}>{children}</Link>
      </Button>
    );

  return <Link {...linkProps}>{children}</Link>;
};
